<template>
  <div class="">
    <page-hero
        class="mb-1"
        :title="msg('Keybox Logs')"
        :subtitle="msg('Access and activity logs for key lockers')"
        variant="keybox"
        :stats="[{ icon: 'ClipboardIcon', value: page.total, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="40" width="60" height="55" rx="4" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.2)" stroke-width="1.5"/>
          <rect x="28" y="50" width="44" height="3" rx="1.5" fill="rgba(255,255,255,.25)"/>
          <rect x="28" y="58" width="36" height="2.5" rx="1.25" fill="rgba(255,255,255,.2)"/>
          <rect x="28" y="66" width="40" height="2.5" rx="1.25" fill="rgba(255,255,255,.2)"/>
          <rect x="28" y="74" width="32" height="2.5" rx="1.25" fill="rgba(255,255,255,.15)"/>
          <circle cx="75" cy="25" r="8" fill="rgba(255,255,255,.08)"/>
          <circle cx="12" cy="90" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <!--Filters-->
    <b-card no-body>
      <b-card-header class="pb-50">
        <b-row no-gutters class="w-100">
          <b-col>
            <h5>{{ msg("Filters") }}</h5>
          </b-col>
          <b-col>
            <div class="d-flex justify-content-end">
              <b-link style="color: inherit" @click="refresh()">
                <feather-icon icon="RefreshCcwIcon" size="16"/>
              </b-link>
            </div>
          </b-col>
        </b-row>
      </b-card-header>
      <b-card-body>
        <b-row>

          <!-- Location Filter -->
          <b-col cols="12" md="3" class="mb-md-0 mb-2">
            <label>{{ msg("Location") }}</label>
            <b-form-input v-model="filter.like.location" class="d-inline-block mr-1"/>
          </b-col>


        </b-row>
      </b-card-body>
    </b-card>

    <!--Keybox Table-->
    <b-card no-body>
      <div class="card-header" style="overflow: hidden">
        <i class="fas fa-key" style="margin-right: 3px"></i>
        <span class="card-title m-0">{{ msg('KeyboxLogs') }}</span>
        <b-col>
          <div class="d-flex justify-content-end">
            <b-link style="color: inherit" @click="refresh()">
              <feather-icon icon="RefreshCcwIcon" size="16"/>
            </b-link>
          </div>
        </b-col>
      </div>

      <!--Card Content-->
      <b-card-body>
        <b-row no-gutters class="mb-1">
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>{{ msg('Show') }}</label>
            <v-select
                v-model="page.amount"
                :options="[10, 25, 50, 100]"
                :clearable="false"
                @option:selected="refresh"
                class="per-page-selector d-inline-block mx-50"
            />
            <label>{{ msg('entries') }}</label>
          </b-col>
          <!-- New Keybox -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button variant="primary" @click="onNewKeybox(0)">
                <i class="fas fa-key" style="margin-right: 2px"></i>
                <span class="text-nowrap">{{ msg('New Keybox') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!--Table-->
        <b-table
            ref="keybox-table"
            striped
            hover
            responsive
            v-bind:items="keyboxLogs"
            v-bind:fields="fields"
            :busy="loading"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mx-1"></b-spinner>
              <strong>{{ msg('Loading') }}...</strong>
            </div>
          </template>

          <template #cell(timestamp)="data">
            {{ $moment(data.value, 'YYYY-MM-DD HH:mm:ss').format("DD.MM.YYYY HH:mm:ss") }}
          </template>

          <template #cell(isClient)="data">
            <b-badge v-if="data.value" variant="success">
              {{ msg('Yes') }}
            </b-badge>
            <b-badge v-else variant="secondary">
              {{ msg('No') }}
            </b-badge>
          </template>

          <template #cell(action)="data">
<b-badge variant="primary">
  {{ data.value.replace(/_/g, ' ') }}
</b-badge>

          </template>

        </b-table>


        <!-- Pagination -->
        <div class="text-center w-100">
          <div class="d-inline-block">
            <b-pagination
                v-model="page.currentPage"
                :total-rows="page.total"
                :per-page="page.amount"
                first-number
                last-number
                @input="refresh"
            />
          </div>
          <div class="d-inline-block px-2"><strong>{{ msg('Total') }}</strong> {{ page.total }}</div>
        </div>
      </b-card-body>
    </b-card>


  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'KeyboxLogs',
  components: { PageHero },
  data() {
    return {
      loading: true,
      keyboxLogs: [],
      fields: [],
      visibleRows: {},
      filter: {
        like: { location: ''},
        in: {}
      },
      sort: {by: 'timestamp', direction: 'desc'},
      page: {amount: 25, currentPage: 1, total: 0},

    }
  },
  created() {
    this.setFields();
    this.refresh();
    this.$root.$on('crm::submitBusinessUnit', (this.listeners.submit = () => {
      this.submit()
    }))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitBusinessUnit', this.listeners.submit)
  },
  computed: {
    ...mapGetters('translator', ['msg']),

  },

  methods: {
    ...mapActions('keybox', ['getAllKeyboxLogs']),

    setFields() {
      this.fields = [
        { key: 'keyBoxAddress', label: this.msg('Address'), sortable: true },
        { key: 'action', label: this.msg('Action'), sortable: true },
        { key: 'slotNumber', label: this.msg('Slot'), sortable: true },
        { key: 'timestamp', label: this.msg('Date'), sortable: true },
        { key: 'performedBy', label: this.msg('Performed by'), sortable: true },
        { key: 'isClient', label: this.msg('Client action'), sortable: true },
        { key: 'details', label: this.msg('Details'), sortable: false },
      ]
    }
    ,
    refresh() {
      this.loading = true;
      this.getAllKeyboxLogs({
        page: this.page.currentPage,
        amount: this.page.amount,
        filter: this.filter,
        sortcolumn: this.sort.by,
        sortby: this.sort.direction,
      }).then(this.onKeyboxLogsRetrieve, (error) => this.error = error)
    },
    onKeyboxLogsRetrieve(data) {
      this.page = {amount: data['Amount'], total: data['Total'], currentPage: data['Page']};
      this.keyboxLogs = data["KeyboxLogs"];
      this.loading = false;
    },

    onSubmitted() {
      this.refresh();
    }
  }
}
</script>
